(ns arthur.domain.span
  "The commands over a node's place in time: split it, trim an edge, move it —
  and, where the symbol holding it is drawn as a lane, re-span its siblings to
  make room.

  A `:span` is in the node's OWN frames and its `:time` says where those land in
  its parent, and that is true of EVERY node. A clip in a sequence, a symbol
  placed straight into a shot, a shape that exists for part of one: each is a
  span in a parent's frame space, and a span in a parent's frame space is the
  whole of what these commands touch.

  THE COORDINATE IS ALWAYS THE PARENT'S. `host-frame` reads the frame space the
  node is positioned in, whatever that is, so a caller holding a node does not
  branch on what it sits in.

  A GROUP IS REFUSED where one node is being divided. Dividing a group means
  deciding what becomes of its children, and nothing in a span says: a span that
  narrows past a child hides it without saying so.

  WHY THE SEQUENCE COMMANDS ARE HERE TOO. They used to be `domain/lane`, gated on
  a group with `:layout :sequence`, because a lane was the only thing anybody had
  timed. There is no such group any more — the SYMBOL is the container and a lane
  is how the timeline draws one, `symbol/lane?` — so their subject is a symbol and
  its children, which is the same subject as everything else here: one write to
  one node's span, with the siblings re-spanned around it. See
  `docs/lane-is-a-view-plan.md`.

  THE CLAIM-TIME RULE FOLLOWS THE MODE. In lane mode, placing, moving or growing
  over occupied frames TRIMS what it lands on — trimming the incumbent, removing
  one wholly covered, splitting one it lands inside — so the result has no overlap
  because the operation that could have made one did not. Outside lane mode
  nothing is enforced, because overlapping children are what compositing IS.

  EVERY COMMAND IS ONE STEP AND ALL OF IT. Each returns `{:clip :selection}` or
  `{:refused reason}` — never a half-applied edit, and never a document that
  `clip/problems` would reject. A command that cannot say what the person meant
  refuses and says why, rather than picking for them: the overflow policy is a
  caller's `:extent`, and decoupling shared content is its own command instead of
  something an ordinary edit does silently.

  IDS COME FROM THE CALLER, because a clip's identity is a uuid and this namespace
  is pure. Ids for new CONTENT are derived from the drawing being copied —
  `clip/free-id` is pure too, and `drawing-a-2` says what it came from in a way
  `symbol-7` does not.

  `finish` lives here because every command in this namespace commits through it."
  (:require [arthur.domain.bring :as bring]
            [arthur.domain.clip :as clip]
            [arthur.domain.node :as node]
            [arthur.domain.symbol :as symbol]))

(defn- fit-lanes
  "Make direct lane children cover `sid`'s authored window.

  A lane has no independently authored extent: it is a view of its parent
  symbol's timeline. Keep that invariant at the one commit point that can grow
  a symbol, so neither the wrapper nor the lane symbol retains an old parent
  length."
  [clip sid]
  (let [frames (clip/frames clip sid)]
    (reduce
     (fn [c [id n]]
       (let [source (node/source n)]
         (if (and (nil? (:parent n))
                  (symbol/lane? (clip/symbol c source)))
           (-> c
               (assoc-in [:symbols sid :nodes id :span] [0 frames])
               (assoc-in [:symbols sid :nodes id :time :at] 0)
               (assoc-in [:symbols source :frames] frames))
           c)))
     clip
     (get-in clip [:symbols sid :nodes]))))

(defn finish
  "Commit `nodes` as symbol `sid`'s, or refuse.

  THE SHOT LENGTH IS AUTHORED. `:frames` is the symbol's window — how long the
  shot IS — and where its clips reach is a different fact derived from them. A
  command may GROW the window when the caller says `:grow-symbol`, and never
  shrinks it: emptying the end of a shot leaves a shot with empty frames at the
  end, which is a true statement about what somebody authored. Deriving the window
  from the reach instead would make deleting the last drawing silently shorten the
  film.

  So there are two numbers and this function keeps them apart: `needed` is where
  the clips reach, `:frames` is what was authored, and the only way the second
  follows the first is a caller asking.

  Only a symbol drawn AS A LANE is measured for reach. A node placed into a
  composition may hang off the end of it — that is an ordinary thing to author and
  the window is what crops it — whereas a lane's clips are a sequence whose length
  is the thing being edited.

  THE OVERLAP INVARIANT IS ENFORCED HERE, and here is the only place it needs to
  be: this is the single commit path for every sequence command, it validates
  before it returns, and it refuses rather than half-applying. So no command can
  commit an overlap in lane mode, and `symbol/overlaps` turning up anything is a
  bug in a command rather than a state to design around."
  [clip sid nodes selection extent]
  (let [sym (clip/symbol clip sid)
        lane? (symbol/lane? sym)
        after (assoc sym :nodes nodes)
        needed (if-not lane?
                 0
                 (js/Math.ceil (apply max 0 (keep #(second (node/placed-span %))
                                                  (symbol/children nodes)))))
        ps (symbol/problems after)
        clashing (when lane? (symbol/overlaps after))]
    (cond
      (seq ps) {:refused (first ps)}
      (seq clashing)
      {:refused (str "that would put " (pr-str (ffirst clashing)) " and "
                     (pr-str (second (first clashing)))
                     " on screen over the same frames of a lane")}
      (not (#{:keep :grow-symbol} extent)) {:refused "choose an explicit shot-length policy"}
      (and (> needed (:frames sym)) (= :keep extent))
      {:refused (str "the edit needs " needed " frames; extend the shot to continue")
       :required-frames needed}
      :else {:clip (fit-lanes
                    (cond-> (assoc-in clip [:symbols sid :nodes] nodes)
                      (> needed (:frames sym))
                      (assoc-in [:symbols sid :frames] needed))
                    sid)
             :selection selection})))

;; ---------------------------------------------------------------------------
;; the geometry every edge edit is made of
;;
;; A `:span` is in the node's OWN frames and its `:time` says where those
;; land in the parent. So moving an edge is one write to `:span`, and `:time`
;; and `:playback` are untouched — which is why trimming the front of a playing
;; insert starts it later in its source instead of resetting it, and why the two
;; halves of a split go on meaning what the one node meant.
;; Trim, split and `blank` are all this one operation, applied differently.

(defn local
  "Parent frame `f` as one of `n`'s own frames."
  [n f]
  (let [{:keys [at rate]} (node/time-of n)]
    (* rate (- f at))))

(defn edged
  "`n` with its `:in` or `:out` edge at parent frame `f`."
  [n which f]
  (assoc-in n [:span (case which :in 0 :out 1)] (local n f)))

(defn- eaten
  "`nodes` with lane clip `id`'s interval taken from every clip not in `keep`:
  those it covers are removed and those crossing an edge trimmed. A clip crossing
  both edges is split, and its remainder is `remainder-id`. Nil where that split
  has no free id."
  [nodes id keep remainder-id]
  (let [[a b] (node/placed-span (get nodes id))
        others (remove #(contains? keep (:id %)) (symbol/children nodes))
        spanning (some #(let [[lo hi] (node/placed-span %)] (and (< lo a) (> hi b))) others)]
    (when-not (and spanning (or (nil? remainder-id) (contains? nodes remainder-id)))
      (reduce
       (fn [ns other]
         (let [oid (:id other)
               [lo hi] (node/placed-span other)]
           (cond
             (or (<= hi a) (>= lo b)) ns
             (and (< lo a) (> hi b))
             (-> ns
                 (assoc oid (edged other :out a))
                 (assoc remainder-id
                        (assoc (edged other :in b) :id remainder-id
                               :z (str "a-" remainder-id))))
             (and (>= lo a) (<= hi b)) (dissoc ns oid)
             (< lo a) (assoc ns oid (edged other :out a))
             :else (assoc ns oid (edged other :in b)))))
       nodes others))))

(defn- lane-child? [clip sid nodes id]
  (let [n (get nodes id)]
    (and (symbol/lane? (clip/symbol clip sid))
         n (nil? (:parent n)) (node/placed-span n))))

(def ^:private split-refusal
  {:refused "claiming time inside one clip needs a free ID for its remainder"})

(defn claim
  "Commit proposed `nodes`, with `id` claiming its interval in lane mode.

  This is the one difference between editing a lane and a composition.  In a
  composition it is exactly `finish`.  In a lane, immediately before that same
  commit, clips covered by the edited one are removed and clips crossing either
  edge are trimmed.  A clip crossing both edges is split and therefore needs a
  caller-supplied `remainder-id`."
  [clip sid nodes id extent remainder-id]
  (if-not (lane-child? clip sid nodes id)
    (finish clip sid nodes id extent)
    (if-let [ns (eaten nodes id #{id} remainder-id)]
      (finish clip sid ns id extent)
      split-refusal)))

(defn claim-each
  "`claim` for clips edited together, `remainders` mapping each one's id to a
  free id for a split. Each takes its interval from the clips that are NOT among
  them; among themselves they keep the places the edit gave them."
  [clip sid nodes remainders extent]
  (let [keep (set (keys remainders))
        ns (reduce (fn [ns [id remainder-id]]
                     (if (lane-child? clip sid ns id)
                       (or (eaten ns id keep remainder-id) (reduced nil))
                       ns))
                   nodes remainders)]
    (if ns (finish clip sid ns nil extent) split-refusal)))

(defn host-frame
  "Symbol frame `f` as a frame of the space node `id` is POSITIONED in — its
  parent's — which is the frame space every command here takes its coordinate
  in. For a clip of a lane that is the symbol's own frames, because the symbol is
  the container and the clip has no parent. Nil through a stepped or looping
  ancestor, where one frame of the symbol is not one frame of the parent and there
  is no single answer to give."
  [clip sid id f]
  (let [nodes (get-in clip [:symbols sid :nodes])]
    (when-let [{:keys [at rate]} (symbol/frame-map nodes (:parent (get nodes id)))]
      (* rate (- f at)))))

(defn- subject
  "The node `id` names, as `{:node n}`, or `{:refused why}` where these commands
  have nothing to act on. The one guard they share."
  [nodes id]
  (let [n (get nodes id)]
    (cond
      (nil? n) {:refused "select something with a place in time"}
      (= :group (:kind n))
      {:refused "a group is divided by its children, not by its span"}
      (nil? (node/placed-span n))
      {:refused "this is on screen for the whole shot, so it has no edges to cut"}
      :else {:node n})))

(defn- siblings
  "The other clips of `sid`'s sequence, or nil where `sid` is not drawn as a lane
  and so has no sequence to re-span."
  [clip sid nodes id]
  (when (symbol/lane? (clip/symbol clip sid))
    (remove #(= id (:id %)) (symbol/children nodes))))

(defn split
  "Cut node `id` in two at parent frame `cut`. The left piece keeps its
  identity; the right gets `new-id`.

  NOTHING BUT `:span` DIFFERS between the two pieces. They keep one `:time`, so
  the right piece's own frames carry on exactly where the left's stopped, and its
  source clock, its keys and its corrections therefore go on meaning what they
  meant before the cut — preserved by construction rather than by arithmetic on
  in-points that could be wrong. A held drawing holds the same frame on both
  sides; a playing insert plays on through the cut without a seam; a shape goes
  on being the same shape over each half. That is what `:span` being in the
  node's OWN coordinates buys, and it is why splitting needs no shot-length
  policy: the pieces occupy the frames the one node occupied.

  THE RIGHT PIECE KEEPS THE ORIGINAL'S `:z`. Two halves of one thing draw at
  one depth; nothing orders them against each other, because they are never on
  screen on the same frame. A lane's clips do not consult `:z` at all —
  `symbol/children` sorts them by where they start.

  The right piece is the selection, because it is the piece that was made."
  [clip sid id cut new-id]
  (let [nodes (get-in clip [:symbols sid :nodes])
        {:keys [node refused]} (subject nodes id)
        [lo hi] (when node (node/placed-span node))]
    (cond
      refused {:refused refused}
      (not (integer? cut)) {:refused "a cut is a whole frame"}
      (contains? nodes new-id) {:refused "the new ID is already used"}
      (not (< lo cut hi)) {:refused (str "frame " cut " is not inside this")}
      :else
      (let [nodes (-> nodes
                      (assoc id (edged node :out cut))
                      (assoc new-id (assoc (edged node :in cut) :id new-id)))]
        (finish clip sid nodes new-id :keep)))))

(defn trim
  "Move one edge of node `id` to parent frame `to`, without disturbing anything
  else at all.

  TRIM NARROWS. Lengthening is `resize-out`, which carries a ripple policy and a
  shot-length policy because it needs them; letting trim grow as well would give
  one gesture two sets of rules and a way to overlap its neighbour. `edge` is
  `:in` or `:out`.

  The source clock is untouched, so trimming the front of a playing insert
  starts it later INTO its animation rather than restarting it — which is the
  difference between trimming and slipping, and why they are separate commands."
  [clip sid id edge to]
  (let [nodes (get-in clip [:symbols sid :nodes])
        {:keys [node refused]} (subject nodes id)
        [lo hi] (when node (node/placed-span node))]
    (cond
      refused {:refused refused}
      (not (#{:in :out} edge)) {:refused "an edge is :in or :out"}
      (not (integer? to)) {:refused "an edge goes to a whole frame"}
      (not (< lo to hi))
      {:refused (str "frame " to " is not inside this; trim narrows it")}
      :else (finish clip sid (assoc nodes id (edged node edge to)) id :keep))))

(defn move
  "Put node `id` at parent frame `to`, leaving its own length, source and
  corrections alone — and, in a lane, every other clip.

  One write to `:time :at`. A destination that would overlap a neighbour IN A
  LANE is refused rather than rippled or overwritten: moving a drawing and
  re-timing the ones around it are different intentions, and a move that
  silently pushed the rest would be the second one wearing the first one's name.
  Clear the room first — `blank` makes a gap, `trim` shortens a neighbour — or
  say you meant to claim it, which is `adopt`, what a body drag does.
  Outside lane mode there is no such rule to break: things placed in a
  composition are allowed to be on screen together, so the move simply happens."
  [clip sid id to]
  (let [nodes (get-in clip [:symbols sid :nodes])
        {:keys [node refused]} (subject nodes id)]
    (cond
      refused {:refused refused}
      (not (integer? to)) {:refused "a move goes to a whole frame"}
      :else
      (let [moved (update-in node [:time :at] (fnil + 0) (- to (first (node/placed-span node))))]
        (if (not= to (first (node/placed-span moved)))
          {:refused "timing through a stepped or looping parent is not supported"}
          (finish clip sid (assoc nodes id moved) id :keep))))))

;; ---------------------------------------------------------------------------
;; the sequence: one edge edit, with the siblings re-spanned around it

(defn- trimmed-into-a-sequence
  "`nodes` with every clip's right edge pulled back to where the next one starts,
  and any clip the next one wholly covers removed.

  THE SAME CLAIM-TIME RULE, APPLIED ALL AT ONCE. Later claims from earlier
  everywhere it has to, which is the rule every other command here follows one
  edit at a time; doing it as a pass is only what makes the answer to \"make this
  a lane\" one undo step."
  [nodes]
  (reduce
   (fn [ns [earlier later]]
     (let [[lo hi] (node/placed-span (get ns (:id earlier)))
           [next-lo _] (node/placed-span later)]
       (cond
         (nil? lo) ns
         (<= hi next-lo) ns
         (<= next-lo lo) (dissoc ns (:id earlier))
         :else (assoc ns (:id earlier) (edged (get ns (:id earlier)) :out next-lo)))))
   nodes
   (partition 2 1 (symbol/children nodes))))

(defn draw-as-lane
  "Turn symbol `sid`'s lane mode on or off. `{:clip c}` or `{:refused why}`.

  OFF IS ALWAYS POSSIBLE: a composition has no invariant to break, so dropping
  the hint drops the rules with it and nothing in the document moves.

  ON IS THE ONE PLACE A PERSON CAN ASK FOR THE IMPOSSIBLE. Every other command
  maintains the sequence; this one asks a symbol whose clips may already be on
  screen together to start being one, and there is no answer that does not throw
  frames away. So it refuses and says how many clips it would have to trim,
  carrying `:required-trim` for the retry the UI offers as one button — the same
  shape as `finish`'s `:required-frames`. With `trim?` it does it: later claims
  from earlier, which is the rule everything else here already follows."
  [clip sid on? {:keys [trim?]}]
  (let [sym (clip/symbol clip sid)]
    (cond
      (nil? sym) {:refused "there is no such symbol"}
      (not on?) {:clip (update-in clip [:symbols sid] dissoc :display)}
      :else
      (let [clashing (symbol/overlaps sym)]
        (cond
          (empty? clashing) {:clip (assoc-in clip [:symbols sid :display] :lane)}
          (not trim?)
          {:refused (str "drawing this as a lane means trimming "
                         (count clashing) " clip"
                         (when (< 1 (count clashing)) "s")
                         " that overlap a neighbour")
           :required-trim (count clashing)}
          :else
          (let [nodes (trimmed-into-a-sequence (:nodes sym))
                after (assoc sym :nodes nodes :display :lane)]
            (if (seq (symbol/overlaps after))
              {:refused "these clips cannot be trimmed into a sequence"}
              {:clip (assoc-in clip [:symbols sid] after)})))))))

(defn resize-out
  "Put clip `id`'s right edge at parent frame `to`, allowing it to grow.

  IN A LANE this claims time. Without `ripple?`, growing consumes the starts of
  the clips it reaches: wholly covered clips disappear and the last partially
  covered one is trimmed. Shrinking leaves a gap. With `ripple?`, every clip
  beginning at or after the old edge moves by the same delta, in either
  direction, so their contents are preserved. Either way there is no overlap,
  because the operation that could have made one did not.

  OUTSIDE A LANE it is one write to one span and nothing else moves, because
  things placed in a composition are allowed to be on screen together. One
  gesture, and the mode says which rule it plays by."
  [clip sid id to {:keys [extent ripple?] :or {extent :keep ripple? false}}]
  (let [nodes (get-in clip [:symbols sid :nodes])
        {:keys [node refused]} (subject nodes id)
        [lo old-out] (when node (node/placed-span node))
        later (when (and node ripple?)
                (filter #(>= (first (node/placed-span %)) old-out)
                        (siblings clip sid nodes id)))]
    (cond
      refused {:refused refused}
      (not (integer? to)) {:refused "an edge goes to a whole frame"}
      (not (< lo to)) {:refused "a clip must keep at least one frame"}
      (= to old-out) {:clip clip :selection id}
      :else
      (let [delta (- to old-out)
            resized (assoc nodes id (edged node :out to))
            changed (reduce (fn [ns sibling]
                              (update-in ns [(:id sibling) :time :at] (fnil + 0) delta))
                            resized later)]
        (claim clip sid changed id extent nil)))))

(defn resize-in
  "Put clip `id`'s left edge at parent frame `to`. Shrinking leaves a gap;
  in a lane, growing left consumes earlier clips symmetrically with `resize-out`."
  [clip sid id to]
  (let [nodes (get-in clip [:symbols sid :nodes])
        {:keys [node refused]} (subject nodes id)
        [old-in hi] (when node (node/placed-span node))]
    (cond
      refused {:refused refused}
      (not (integer? to)) {:refused "an edge goes to a whole frame"}
      (not (< to hi)) {:refused "a clip must keep at least one frame"}
      (neg? to) {:refused "a clip cannot begin before the shot"}
      (= to old-in) {:clip clip :selection id}
      :else (claim clip sid (assoc nodes id (edged node :in to)) id :keep nil))))

(defn roll
  "Move the shared boundary between adjacent clips `left-id` and `right-id`.
  This is deliberately only the composition of the two ordinary edge edits."
  [clip sid left-id right-id to]
  (let [nodes (get-in clip [:symbols sid :nodes])
        left (get nodes left-id)
        right (get nodes right-id)
        [llo lhi] (when left (node/placed-span left))
        [rlo rhi] (when right (node/placed-span right))]
    (cond
      (not (symbol/lane? (clip/symbol clip sid)))
      {:refused "a rolling edit needs a symbol drawn as a lane"}
      (not (and llo rlo (nil? (:parent left)) (nil? (:parent right))))
      {:refused "a rolling edit needs two clips of one sequence"}
      (not= lhi rlo) {:refused "a rolling edit needs one shared boundary"}
      (not (integer? to)) {:refused "a clip edge goes to a whole frame"}
      (not (< llo to rhi)) {:refused "both clips must keep at least one frame"}
      :else
      (let [left-result (resize-out clip sid left-id to {})]
        (if (:refused left-result)
          left-result
          (resize-in (:clip left-result) sid right-id to))))))

(defn blank
  "Clear frames `[a b)` of symbol `sid`, leaving a GAP.

  A gap is not a drawing. Nothing is invented to cover those frames and nothing
  closes the hole — the clips after it stay where they are, because emptying
  frames and re-timing a performance are different intentions.

  What it does to each clip it meets is `edged`, applied three ways: one wholly
  inside is removed, one overlapping an end is trimmed to it, and the one that
  spans the whole range is split, which is the only case that needs `id`. Their
  drawings stay in the library — a symbol does not own its content, and a drawing
  whose last clip is gone is still a drawing somebody made."
  [clip sid [a b] {:keys [id]}]
  (let [nodes (get-in clip [:symbols sid :nodes])
        lane? (symbol/lane? (clip/symbol clip sid))
        members (when lane? (symbol/children nodes))
        spanning (when members
                   (first (filter #(let [[lo hi] (node/placed-span %)] (and (< lo a) (> hi b)))
                                  members)))]
    (cond
      (not lane?) {:refused "clearing a range of frames needs a symbol drawn as a lane"}
      (not (and (integer? a) (integer? b) (< a b)))
      {:refused "a range to blank is whole frames, and not empty"}
      (and spanning (or (nil? id) (contains? nodes id)))
      {:refused "blanking inside one clip splits it, which needs a free ID for the remainder"}
      :else
      (let [nodes (reduce
                   (fn [ns n]
                     (let [[lo hi] (node/placed-span n)]
                       (cond
                         (or (<= hi a) (>= lo b)) ns
                         (and (< lo a) (> hi b))
                         (-> ns
                             (assoc (:id n) (edged n :out a))
                             (assoc id (assoc (edged n :in b) :id id :z (str "a-" id))))
                         (and (>= lo a) (<= hi b)) (dissoc ns (:id n))
                         (< lo a) (assoc ns (:id n) (edged n :out a))
                         :else (assoc ns (:id n) (edged n :in b)))))
                   nodes members)]
        ;; NOTHING SENSIBLE IS SELECTED by emptying frames, so nothing is: a
        ;; split names its remainder, and otherwise the caller keeps whatever was
        ;; selected rather than being handed a clip it did not ask for.
        (finish clip sid nodes (when spanning id) :keep)))))

(defn extend-hold
  "Change one held clip's duration by `delta` frames and ripple its later
  siblings. Keys, source clocks and the clips' own channels stay put.
  Returns {:clip :selection} or {:refused :required-frames?}; never partially edits."
  [clip sid id delta {:keys [extent] :or {extent :keep}}]
  (let [nodes (get-in clip [:symbols sid :nodes])
        n (get nodes id)
        rate (:rate (node/time-of n))
        span (:span n)]
    (cond
      (not (symbol/lane? (clip/symbol clip sid)))
      {:refused "a hold is lengthened in a symbol drawn as a lane"}
      (nil? (node/placed-span n)) {:refused "select a clip in the lane"}
      (some? (:parent n)) {:refused "select a clip of the lane itself"}
      (not (and (integer? delta) (not (zero? delta)))) {:refused "hold change must be a nonzero whole number of frames"}
      (not (zero? (:speed (node/playback-of n)))) {:refused "hold length applies to a held drawing"}
      (<= (+ (second span) (* rate delta)) (first span)) {:refused "a drawing must keep a positive exposure"}
      :else
      (let [[_ boundary] (node/placed-span n)
            later (filter #(>= (first (node/placed-span %)) boundary)
                          (siblings clip sid nodes id))
            nodes (assoc-in nodes [id :span 1] (+ (second span) (* rate delta)))
            nodes (reduce (fn [ns sibling]
                            (update-in ns [(:id sibling) :time :at] (fnil + 0) delta))
                          nodes later)]
        (finish clip sid nodes id extent)))))

(defn placement-refusal
  "Why direct child `n` may not be placed in symbol `sid`, or nil.

  This is the type-containment boundary for every placement path. Specialized
  symbols are confined here rather than by drag affordances, so pool drops,
  transfers, structural nesting and future commands cannot disagree."
  [clip sid n]
  (let [destination (clip/symbol clip sid)
        source (when (= :instance (:kind n)) (clip/symbol clip (node/source n)))
        destination-type (:type destination)
        source-type (:type source)]
    (cond
      (= :palette destination-type) "a palette symbol cannot contain nodes"
      (and (= :palette-track destination-type) (not= :palette source-type))
      "a palette track accepts only palette symbols"
      (and (= :palette source-type) (not= :palette-track destination-type))
      "a palette symbol can be placed only in a palette track"
      :else nil)))

(defn place-node
  "Place the already-materialized, direct child `n` into symbol `sid` at `at`.

  THIS IS THE PLACEMENT RULE. Materializing a symbol instance, an audio node, or
  imported content is deliberately somebody else's job; once it is a node, its
  origin no longer matters. The destination alone decides the edit:

  - an ordinary symbol attaches it and permits overlap;
  - a lane symbol first clears the interval it claims.

  The caller hands this function a node not currently present in the destination.
  Its duration, channels, source clock and identity are preserved; only its
  parent-space start changes. Returns the ordinary command result shape."
  [clip sid n at {:keys [extent remainder-id] :or {extent :keep}}]
  (let [sym (clip/symbol clip sid)
        nodes (:nodes sym)
        id (:id n)
        [lo hi] (when n (node/placed-span n))
        duration (when (and lo hi) (- hi lo))
        incompatible (when (and sym n) (placement-refusal clip sid n))]
    (cond
      (nil? sym) {:refused "there is no destination symbol"}
      (nil? n) {:refused "there is no clip to place"}
      incompatible {:refused incompatible}
      (contains? nodes id) {:refused "the destination already uses that clip ID"}
      (some? (:parent n)) {:refused "only a direct child can be placed in a symbol"}
      (not (and (integer? at) (not (neg? at))))
      {:refused "a position is a nonnegative whole frame"}
      (not (pos? duration)) {:refused "the clip has no frames to place"}
      (and remainder-id (contains? nodes remainder-id))
      {:refused "the remainder clip needs a free ID"}
      :else
      (let [placed (update-in n [:time :at] (fnil + 0) (- at lo))]
        (claim clip sid (assoc nodes id placed) id extent remainder-id)))))

(defn place-symbol
  "Materialize an instance of `source-id`, then place it into `sid` at `at`.

  IN A LANE the new clip claims its interval: existing clips under it are
  trimmed, removed, or split, so the sequence stays a partition rather than
  storing an overlap. Outside one it is simply placed. This is the generic
  operation behind dropping a library symbol into the timeline; one-frame held
  drawing creation remains a policy of `append-drawing`/`overwrite-drawing`,
  not a different kind of container."
  [clip store sid id source-id at
   {:keys [extent point remainder-id speed] :or {extent :keep speed 1}}]
  (let [nodes (get-in clip [:symbols sid :nodes])
        seeded (clip/place-symbol clip store sid source-id 0 id point speed)
        n (get-in seeded [:symbols sid :nodes id])]
    (cond
      (contains? nodes id) {:refused "the new clip ID is already used"}
      (nil? (clip/symbol clip source-id)) {:refused "there is no such symbol to place"}
      (nil? n) {:refused "a symbol cannot go inside itself"}
      :else
      (place-node clip sid n at {:extent extent :remainder-id remainder-id}))))

(defn adopt
  "Move an existing clip of `sid` to frame `at`, CLAIMING the time it lands on.

  Its source, span, transforms, corrections, and identity come with it; in a lane
  the destination interval is claimed with the same trimming as a pool drop. This
  is what a body drag does, and it is why `move` and this are two commands:
  `move` refuses to disturb a neighbour, and a drag onto occupied time has
  already said it means to."
  [clip sid id at opts]
  (let [nodes (get-in clip [:symbols sid :nodes])
        n (get nodes id)]
    (cond
      (nil? n) {:refused "select a clip to move"}
      (some? (:parent n)) {:refused "only a clip of the symbol itself is placed in its sequence"}
      :else
      (place-node (assoc-in clip [:symbols sid :nodes] (dissoc nodes id))
                  sid n at opts))))

(defn transfer
  "Move direct child `id` from `from-sid` into `to-sid` at `at`.

  Cross-symbol and same-symbol moves are deliberately the same composition:
  detach, then `place-node`. The destination's mode—not the gesture, source, or
  payload kind—decides whether occupied time is claimed."
  [clip from-sid id to-sid at opts]
  (let [from-nodes (get-in clip [:symbols from-sid :nodes])
        n (get from-nodes id)]
    (cond
      (nil? n) {:refused "select a clip to move"}
      (some? (:parent n)) {:refused "only a direct child can move between symbols"}
      :else
      (let [detached (assoc-in clip [:symbols from-sid :nodes] (dissoc from-nodes id))
            result (place-node detached to-sid n at opts)]
        (if-let [made (:clip result)]
          (if-let [why (first (clip/problems made))]
            {:refused why}
            result)
          result)))))

;; ---------------------------------------------------------------------------
;; putting drawings in a sequence

(defn- held
  "A one-frame held clip of `drawing-id`, starting at frame `at`.

  Held rather than playing, and one frame rather than the length of what it
  places: a clip's duration is the sequence's business — `extend-hold` and
  `resize-out` are how it changes — and reading it off the content would make
  placing a ten-frame animation and holding its first drawing the same gesture.

  NO PIVOT, unlike `clip/place-symbol`, and the difference is whether the content
  EXISTS YET. Dropping a symbol onto the stage places a drawing somebody can see,
  so the middle of it is known and is stored as the instance's pivot; a cel is
  made to be drawn in, and the middle of an empty drawing is nothing to commit to.
  So a cel's pivot stays unchosen until a turn or a scale chooses it, which is
  `gesture/with-pivot`, from the bounds the drawing has by then."
  [id drawing-id at]
  {:id id :kind :instance :z (str "a-" id)
   :span [0 1] :time {:at at :rate 1}
   :source {:symbol drawing-id} :playback {:in 0 :speed 0 :end :stop}})

(defn- sequence-end
  "Where `sid`'s occupied frames stop."
  [nodes]
  (apply max 0 (map #(second (node/placed-span %)) (symbol/children nodes))))

(defn- place
  "Put a held clip of `drawing-id` into `sid` at frame `at`, and RIPPLE:
  everything starting at or after it moves later by its duration.

  There is one placement function and `:end` is a position like any other, so
  appending is not a different operation from inserting — the end is just where
  nothing has to move. Overwriting is the other policy and is NOT this: taking
  frames away from the clip already there is trimming, which is its own command
  and not something placing a drawing should do on the quiet.

  `:frame` in the result is where it landed, in the symbol's own frames, for a
  caller that wants to look at what it just made."
  [clip sid id drawing-id at extent ripple?]
  (let [nodes (get-in clip [:symbols sid :nodes])
        at (if (= :end at) (sequence-end nodes) at)
        n (held id drawing-id at)
        [lo hi] (node/placed-span n)
        ;; RIPPLE FOLLOWS THE MODE. Pushing later siblings is what inserting
        ;; into a sequence means; in a composition there is no "later sibling"
        ;; to push, because being on screen together is the point.
        later (when (and ripple? (symbol/lane? (clip/symbol clip sid)))
                (filter #(>= (first (node/placed-span %)) lo) (symbol/children nodes)))
        nodes (reduce (fn [ns sibling]
                        (update-in ns [(:id sibling) :time :at] (fnil + 0) (- hi lo)))
                      (assoc nodes id n) later)
        result (finish clip sid nodes id extent)]
    (cond-> result
      (:clip result) (assoc :frame at))))

(defn- placeable
  "Why a held clip cannot go into `sid` at `at`, or nil."
  [clip sid id at]
  (let [nodes (get-in clip [:symbols sid :nodes])
        ;; INSIDE a clip is not a position for another one, IN A LANE. Splitting
        ;; that clip is what makes it two, and doing it here would be one command
        ;; quietly performing two: the caller asks for `split` and then places.
        ;; In a composition landing inside something is not a collision at all.
        inside (when (and (number? at) (symbol/lane? (clip/symbol clip sid)))
                 (some (fn [n] (let [[lo hi] (node/placed-span n)]
                                 (when (< lo at hi) n)))
                       (symbol/children nodes)))]
    (cond
      (contains? nodes id) "the new clip ID is already used"
      (not (or (= :end at) (and (integer? at) (not (neg? at)))))
      "a position is :end or a whole frame"
      inside (str "frame " at " is inside a clip; split it first"))))

(defn append-drawing
  "Append fresh empty content and a held clip of it. IDs come from the
  caller so a command is deterministic and replayable.

  Fresh content, not a blank range: a sequence with no clip over a frame shows
  nothing there already, and a drawing nobody has drawn in is a different thing
  from a gap."
  [clip sid id drawing-id {:keys [at extent] :or {extent :keep at :end}}]
  (if-let [why (or (placeable clip sid id at)
                   (when (clip/symbol clip drawing-id) "the new drawing ID is already used"))]
    {:refused why}
    (place (assoc-in clip [:symbols drawing-id]
                     {:id drawing-id :frames 1 :nodes {}})
           sid id drawing-id at extent true)))

(defn reuse-drawing
  "Append a held clip of content the document ALREADY has, so the same
  drawing is exposed twice and editing it changes both clips.

  This is the command `make-unique` is the undo of, and the reason they are two
  commands: reuse is a decision to share, and sharing is not something to
  discover later when an edit turns up somewhere else."
  [clip sid id drawing-id {:keys [at extent] :or {extent :keep at :end}}]
  (if-let [why (or (placeable clip sid id at)
                   (when-not (clip/symbol clip drawing-id) "there is no such drawing to reuse")
                   ;; Placing something that contains this symbol would close a
                   ;; loop, and a sequence is no different from any other placement.
                   (when (clip/contains-symbol? clip drawing-id sid)
                     "a symbol cannot go inside itself"))]
    {:refused why}
    (place clip sid id drawing-id at extent true)))

(defn- copied
  "A copy of symbol `from`, as `{:clip :id}`.

  SHALLOW by default: its own nodes and channels are copied, and its references
  to other symbols are kept, so a head built out of reusable eyes still uses
  those eyes. `deep?` copies everything it places as well, with new ids
  throughout, for a drawing that must share nothing — the distinction the
  shallow copy cannot make on its own, and a promise of independence that only
  the deep one keeps."
  [clip from deep?]
  (if deep?
    (let [{c :clip ids :ids} (bring/symbols clip clip [from] {})]
      {:clip c :id (ids from)})
    (let [id (clip/free-id (:symbols clip) from)]
      {:clip (assoc-in clip [:symbols id] (assoc (clip/symbol clip from) :id id))
       :id id})))

(defn duplicate-drawing
  "Append a held clip of a COPY of what clip `id` places, for when
  the drawing on screen is the starting point for the next one.

  The copy is of the content only. The new clip is a plain one-frame hold
  rather than a copy of `id`'s own transform or corrections: those belong to
  that clip, and carrying them over would make duplicating a drawing quietly
  duplicate the treatment of one use of it."
  [clip sid id new-id {:keys [at extent deep?] :or {extent :keep at :end}}]
  (let [n (get-in clip [:symbols sid :nodes id])
        from (node/source n)]
    (if-let [why (or (when-not from "select a clip to duplicate")
                     (when-not (clip/symbol clip from) "the drawing it places is missing")
                     (placeable clip sid new-id at))]
      {:refused why}
      (let [{c :clip copy :id} (copied clip from deep?)]
        (place c sid new-id copy at extent true)))))

(defn overwrite-drawing
  "Put a fresh one-frame drawing at frame `at`, replacing whatever was there and
  leaving every other clip where it was.

  This is `blank` and placement composed in ONE command and therefore one undo
  step. `remainder-id` is used only when clearing the frame cuts one clip into
  two; ids still come from the caller because this namespace is pure."
  [clip sid id drawing-id at {:keys [extent remainder-id] :or {extent :keep}}]
  (let [nodes (get-in clip [:symbols sid :nodes])]
    (if-let [why (cond
                   (not (and (integer? at) (not (neg? at))))
                   "a position is a nonnegative whole frame"
                   (contains? nodes id) "the new clip ID is already used"
                   (or (= id remainder-id) (contains? nodes remainder-id))
                   "the remainder clip needs a free ID different from the new clip"
                   (clip/symbol clip drawing-id) "the new drawing ID is already used")]
      {:refused why}
      (let [c (assoc-in clip [:symbols drawing-id]
                        {:id drawing-id :frames 1 :nodes {}})
            nodes (assoc nodes id (held id drawing-id at))]
        (claim c sid nodes id extent remainder-id)))))

(defn drawing-at
  "A new drawing in lane `sid` from frame `f`, as Flash's F6 and F7 make one:
  a copy of the drawing showing there when `copy?`, an empty one when not.

  IT TAKES OVER FROM `f`. Inside a hold, the held cel ends at `f` and the new
  one has the rest of it, so the timing already there is kept and only the
  drawing changes. In a gap it runs to the next cel; past the last one it is one
  frame. On a cel's first frame there is a drawing starting there already, so
  the new one goes on the frame after — pressing F6 again and again draws on,
  one frame at a time — and shows the drawing it came after.

  The copy is of the content, shallow, as `duplicate-drawing`'s is, under a name
  of its own. `:frame` in the result is where it landed."
  [clip sid f new-id drawing-id {:keys [copy? extent] :or {extent :keep}}]
  (let [nodes (get-in clip [:symbols sid :nodes])
        cels (symbol/children nodes)
        lo-of #(first (node/placed-span %))
        hi-of #(second (node/placed-span %))
        on (some #(when (= f (lo-of %)) %) cels)
        f (if on (inc f) f)
        holding (some #(when (< (lo-of %) f (hi-of %)) %) cels)
        shown (or holding on (last (filter #(<= (hi-of %) f) cels)))
        next-lo (some #(when (<= f (lo-of %)) (lo-of %)) cels)
        from (when copy? (some-> shown node/source))]
    (cond
      (not (and (integer? f) (not (neg? f)))) {:refused "a position is a nonnegative whole frame"}
      (contains? nodes new-id) {:refused "the new clip ID is already used"}
      (clip/symbol clip drawing-id) {:refused "the new drawing ID is already used"}
      (and copy? (not (clip/symbol clip from))) {:refused "there is no drawing here to copy"}
      :else
      (let [c (assoc-in clip [:symbols drawing-id]
                        (if from
                          ;; No name: it is a new drawing, named by where it
                          ;; is drawn, and two of one name is a question nobody asked.
                          (-> (clip/symbol clip from) (assoc :id drawing-id) (dissoc :name))
                          {:id drawing-id :frames 1 :nodes {}}))]
        (if (= f next-lo)
          ;; A cel starts right here, so there is no room: make some.
          (place c sid new-id drawing-id f extent true)
          (let [end (cond holding (hi-of holding) next-lo next-lo :else (inc f))
                nodes (cond-> (assoc nodes new-id (assoc (held new-id drawing-id f) :span [0 (- end f)]))
                        holding (assoc (:id holding) (edged holding :out f)))]
            (let [result (finish c sid nodes new-id extent)]
              (cond-> result (:clip result) (assoc :frame f)))))))))

(defn make-unique
  "Point clip `id` at a private copy of its content, leaving every other
  clip of that drawing sharing the original.

  Refused when nothing else uses it: a drawing with one clip is already
  unique, and answering with a silent copy would leave a second identical symbol
  in the library for no reason a person could see."
  [clip sid id {:keys [deep?]}]
  (let [n (get-in clip [:symbols sid :nodes id])
        from (node/source n)
        elsewhere (for [[osid osym] (:symbols clip)
                        [oid on] (:nodes osym)
                        :when (and (= from (node/source on)) (not= [sid id] [osid oid]))]
                    [osid oid])]
    (if-let [why (or (when-not from "select a clip to make unique")
                     (when-not (clip/symbol clip from) "the drawing it places is missing")
                     (when (empty? elsewhere) "nothing else uses this drawing"))]
      {:refused why}
      (let [{c :clip copy :id} (copied clip from deep?)
            c (assoc-in c [:symbols sid :nodes id :source :symbol] copy)
            ps (clip/problems c)]
        (if (seq ps) {:refused (first ps)} {:clip c :selection id})))))
